import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import SaleForm from "../components/SalesExecutive/SaleForm";
import { getEligibleSalesVisits } from "../services/api";
import { useNotification } from "../hooks/useNotification";

export default function RecordSale({
  outlet: outletProp,
  onSave,
  mode = "sale",
  embedded = false,
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const isReturnMode = mode === "return";
  const { showSuccess, showError } = useNotification();

  const [eligibleVisits, setEligibleVisits] = useState([]);
  const [selectedVisitId, setSelectedVisitId] = useState("");
  const [loadingVisits, setLoadingVisits] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [isChoosingVisit, setIsChoosingVisit] = useState(false);
  const formStartRef = useRef(null);

  useEffect(() => {
    if (outletProp?.check_in_id) {
      setEligibleVisits([outletProp]);
      setSelectedVisitId(String(outletProp.check_in_id));
      setLoadingVisits(false);
      return undefined;
    }

    let isMounted = true;

    const loadEligibleVisits = async () => {
      try {
        setLoadingVisits(true);
        const result = await getEligibleSalesVisits({ hours: 24 });
        if (!isMounted) return;

        const visits = Array.isArray(result?.data) ? result.data : [];
        setEligibleVisits(visits);
        setSelectedVisitId("");
      } catch (error) {
        if (!isMounted) return;
        console.error("Failed to fetch eligible sales visits:", error);
        setEligibleVisits([]);
        setSelectedVisitId("");
      } finally {
        if (isMounted) {
          setLoadingVisits(false);
        }
      }
    };

    loadEligibleVisits();

    return () => {
      isMounted = false;
    };
  }, [outletProp]);

  const displayVisits = useMemo(() => {
    const visitMap = new Map();

    eligibleVisits.forEach((visit) => {
      const outletKey = String(
        visit.outlet_id ||
          visit.bp_outlet_id ||
          `${visit.outlet_name || ""}|${visit.outlet_address || ""}`,
      );
      const existingVisit = visitMap.get(outletKey);

      if (!existingVisit) {
        visitMap.set(outletKey, visit);
        return;
      }

      const existingPriority = existingVisit.is_active ? 2 : 1;
      const currentPriority = visit.is_active ? 2 : 1;
      const existingTime = new Date(
        existingVisit.checked_out_at || existingVisit.checked_in_at || 0,
      ).getTime();
      const currentTime = new Date(
        visit.checked_out_at || visit.checked_in_at || 0,
      ).getTime();

      if (
        currentPriority > existingPriority ||
        (currentPriority === existingPriority && currentTime > existingTime)
      ) {
        visitMap.set(outletKey, visit);
      }
    });

    return Array.from(visitMap.values());
  }, [eligibleVisits]);

  useEffect(() => {
    if (outletProp?.check_in_id) {
      setIsChoosingVisit(false);
      return;
    }

    if (loadingVisits) return;

    if (displayVisits.length === 1) {
      setSelectedVisitId(String(displayVisits[0].check_in_id));
      setIsChoosingVisit(false);
      return;
    }

    if (
      selectedVisitId &&
      displayVisits.some(
        (visit) => String(visit.check_in_id) === String(selectedVisitId),
      )
    ) {
      return;
    }

    setSelectedVisitId("");
    setIsChoosingVisit(displayVisits.length > 1);
  }, [displayVisits, loadingVisits, outletProp, selectedVisitId]);

  const activeCheckIn = useMemo(() => {
    if (outletProp?.check_in_id) return outletProp;

    return (
      displayVisits.find(
        (visit) => String(visit.check_in_id) === String(selectedVisitId),
      ) || null
    );
  }, [displayVisits, outletProp, selectedVisitId]);

  useEffect(() => {
    if (!activeCheckIn || isChoosingVisit) return;

    const timer = window.setTimeout(() => {
      formStartRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }, 120);

    return () => window.clearTimeout(timer);
  }, [activeCheckIn, isChoosingVisit]);

  const handleSubmit = async (saleData) => {
    setSubmitError(null);
    if (!saleData?.items || saleData.items.length === 0) {
      setSubmitError("Add at least one item");
      return;
    }

    if (loadingVisits) {
      setSubmitError(t("se.sales.loadingVisits"));
      return;
    }

    if (!activeCheckIn || !activeCheckIn.check_in_id) {
      setSubmitError(t("se.sales.noEligibleVisitsMessage"));
      return;
    }

    try {
      setSubmitting(true);

      const formData = new FormData();
      formData.append("check_in_id", activeCheckIn.check_in_id);
      formData.append(
        "items",
        JSON.stringify(
          saleData.items.map((item) => ({
            product_id: item.product_id,
            product_name: item.product_name,
            quantity: item.quantity,
            unit_price: parseFloat(item.unit_price) || 0,
            serial_number: item.serial_number || null,
          })),
        ),
      );

      if (saleData.notes) {
        formData.append("notes", saleData.notes);
      }

      if (saleData.receiptNumber) {
        formData.append("receipt_number", saleData.receiptNumber);
      }

      if (saleData.receiptFile) {
        formData.append("receipt", saleData.receiptFile);
      }

      const token = localStorage.getItem("token");
      const endpoint = isReturnMode ? "/api/sales/returns" : "/api/sales";
      const response = await fetch(endpoint, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
        },
        body: formData,
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(
          errorData.error || `Failed to save sale: ${response.status}`,
        );
      }

      showSuccess(
        isReturnMode
          ? t("se.sale.successReturnSaved")
          : t("se.sale.successSaved"),
      );

      if (typeof onSave === "function") {
        onSave({ mode });
      } else {
        navigate("/promoter", {
          state: { view: "DASHBOARD" },
        });
      }
    } catch (err) {
      console.error("Save sale error:", err);
      const message = err.message || "Failed to save sale";
      setSubmitError(message);
      showError(message);
    } finally {
      setSubmitting(false);
    }
  };

  const renderVisitSelector = () => (
    <div className="se-sales-visit-card">
      <div className="se-sales-visit-card-header">
        <h3>
          {isReturnMode
            ? t("se.sales.return.visitSelectorTitle", {
                defaultValue: "Select Outlet for Return",
              })
            : t("se.sales.visitSelectorTitle")}
        </h3>
        <p>
          {isReturnMode
            ? t("se.sales.return.visitSelectorSubtitle", {
                defaultValue:
                  "Choose the outlet from your latest active or recent visit, then record the returned item there.",
              })
            : t("se.sales.visitSelectorSubtitle")}
        </p>
      </div>

      {loadingVisits ? (
        <div className="se-sales-visit-skeleton" aria-hidden="true">
          <div className="se-sales-visit-skeleton-line se-sales-visit-skeleton-line-lg"></div>
          <div className="se-sales-visit-skeleton-line"></div>
          <div className="se-sales-visit-skeleton-line se-sales-visit-skeleton-line-sm"></div>
        </div>
      ) : displayVisits.length === 0 ? (
        null
      ) : (
        <div className="se-sales-visit-options">
          {displayVisits.map((visit) => {
            const isSelected =
              String(visit.check_in_id) === String(selectedVisitId);

            return (
              <button
                key={visit.check_in_id}
                type="button"
                className={`se-sales-visit-option ${isSelected ? "selected" : ""}`}
                onClick={() => {
                  setSelectedVisitId(String(visit.check_in_id));
                  setIsChoosingVisit(false);
                }}
              >
                <div className="se-sales-visit-option-top">
                  <span className="se-sales-visit-option-name">
                    {visit.outlet_name}
                  </span>
                  <span
                    className={`se-sales-visit-status ${
                      visit.is_active ? "active" : "completed"
                    }`}
                  >
                    {visit.is_active
                      ? t("se.sales.visitActive")
                      : t("se.sales.visitCompleted")}
                  </span>
                </div>
                <div className="se-sales-visit-option-address">
                  {visit.outlet_address || t("se.sales.noAddress")}
                </div>
                <div className="se-sales-visit-option-time">
                  {visit.is_active
                    ? t("se.sales.checkedInAt", {
                        time: new Date(visit.checked_in_at).toLocaleString(),
                      })
                    : t("se.sales.checkedOutAt", {
                        time: new Date(visit.checked_out_at).toLocaleString(),
                      })}
                </div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );

  return (
    <div className="se-sales-entry-view">
      {!outletProp?.check_in_id &&
      (loadingVisits || isChoosingVisit || (!activeCheckIn && displayVisits.length > 0))
        ? renderVisitSelector()
        : null}

      {activeCheckIn && !isChoosingVisit && (
        <div
          className={`se-sales-selected-visit ${
            isReturnMode ? "se-sales-selected-visit--return" : ""
          }`}
        >
          <div className="se-sales-selected-visit-top">
            <div>
              <div className="se-sales-selected-visit-label">
                {t("se.sales.selectedVisit")}
              </div>
              <div className="se-sales-selected-visit-name">
                {activeCheckIn.outlet_name}
              </div>
            </div>
          </div>
          <div className="se-sales-selected-visit-address">
            {activeCheckIn.outlet_address || t("se.sales.noAddress")}
          </div>
          {!outletProp?.check_in_id && displayVisits.length > 1 && (
            <div className="se-sales-selected-visit-actions">
              <button
                type="button"
                className="se-sales-change-visit-btn"
                onClick={() => setIsChoosingVisit(true)}
              >
                {isReturnMode
                  ? t("se.sales.return.changeOutlet", {
                      defaultValue: "Change Outlet",
                    })
                  : t("se.sales.changeVisit")}
              </button>
            </div>
          )}
        </div>
      )}

      {activeCheckIn && !isChoosingVisit ? (
        <div ref={formStartRef}>
          <SaleForm
            mode={mode}
            requireReceipt={false}
            onCancel={() => {
              if (embedded && typeof onSave === "function") {
                onSave({ cancelled: true, mode });
                return;
              }

              navigate("/promoter", { replace: true });
            }}
            onSubmit={handleSubmit}
            submitting={submitting}
            formError={submitError}
          />
        </div>
      ) : !loadingVisits &&
        !isChoosingVisit &&
        displayVisits.length === 0 ? (
        <div className="se-empty-tab">
          <div className="se-empty-tab-card">
            <h3>
              {isReturnMode
                ? t("se.sales.return.noOutletTitle", {
                    defaultValue: "No outlet available for return",
                  })
                : t("se.sales.noEligibleVisitsTitle")}
            </h3>
            <p>
              {isReturnMode
                ? t("se.sales.return.noOutletMessage", {
                    defaultValue:
                      "You need an active visit or a recent checked-out visit before recording a return.",
                  })
                : t("se.sales.noEligibleVisitsMessage")}
            </p>
          </div>
        </div>
      ) : null}
    </div>
  );
}
